import React, { useState, useRef, useEffect, useCallback } from 'react';
import './DijkstraGenerator.css';

const DijkstraGenerator = () => {
  const graphvizContainerRef = useRef(null);
  const graphvizRendererRef = useRef(null);
  const [config, setConfig] = useState({
    nodes: 12,
    edges: 22,
    layers: 2,
    seed: '12345'
  });
  const [appState, setAppState] = useState({
    g: null,
    alg: null,
    seedStr: '12345',
    showingSolution: false
  });
  const [output, setOutput] = useState('');
  const [exporting, setExporting] = useState(false);
  const [librariesReady, setLibrariesReady] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Load D3 and Graphviz via CDN
  useEffect(() => {
    const checkLibraries = () => {
      // Check if d3 is available and has the graphviz method
      return window.d3 && window.d3.select && typeof window.d3.select('body').graphviz === 'function';
    };

    // Check if already loaded
    if (checkLibraries()) {
      console.log('Libraries already loaded');
      setLibrariesReady(true);
      return;
    }

    const d3Script = document.createElement('script');
    d3Script.src = 'https://unpkg.com/d3@7';
    d3Script.async = false; // Load synchronously to ensure order

    const graphvizScript = document.createElement('script');
    graphvizScript.src = 'https://unpkg.com/d3-graphviz@5.0.2/build/d3-graphviz.js';
    graphvizScript.async = false; // Load synchronously

    d3Script.onload = () => {
      console.log('D3 loaded');
      document.body.appendChild(graphvizScript);

      graphvizScript.onload = () => {
        console.log('Graphviz script loaded');
        // Wait a bit for graphviz to attach to d3
        setTimeout(() => {
          if (checkLibraries()) {
            console.log('Graphviz method available on d3');
            setLibrariesReady(true);
          } else {
            console.error('Graphviz loaded but method not available on d3');
            setLoadError('Failed to load Graphviz library. Please refresh the page.');
          }
        }, 100);
      };

      graphvizScript.onerror = () => {
        console.error('Failed to load Graphviz script');
        setLoadError('Failed to load Graphviz library from CDN. Please check your internet connection.');
      };
    };

    d3Script.onerror = () => {
      console.error('Failed to load D3 script');
      setLoadError('Failed to load D3 library from CDN. Please check your internet connection.');
    };

    document.body.appendChild(d3Script);

    return () => {
      if (d3Script.parentNode) document.body.removeChild(d3Script);
      if (graphvizScript.parentNode) document.body.removeChild(graphvizScript);
    };
  }, []);

  // RNG & helpers
  function xmur3(str) {
    let h = 1779033703 ^ (str.length >>> 0);
    for (let i = 0; i < str.length; i++) {
      h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
      h = (h << 13) | (h >>> 19);
    }
    return function() {
      h = Math.imul(h ^ (h >>> 16), 2246822507);
      h = Math.imul(h ^ (h >>> 13), 3266489909);
      return (h ^= h >>> 16) >>> 0;
    };
  }

  function mulberry32(a) {
    return function() {
      let t = a += 0x6D2B79F5;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function makeRng(seedStr, attempt = 0) {
    const seedFn = xmur3(String(seedStr));
    const s1 = seedFn() ^ (attempt * 0x9E3779B1);
    return mulberry32(s1 >>> 0);
  }

  function randInt(rng, lo, hi) {
    return Math.floor(rng() * (hi - lo + 1)) + lo;
  }

  function shuffle(rng, arr) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  function labelForIndex(idx) {
    let n = idx, s = "";
    do {
      s = String.fromCharCode(65 + (n % 26)) + s;
      n = Math.floor(n / 26) - 1;
    } while (n >= 0);
    return s;
  }

  function pathToLabelString(path, labels) {
    if (!path || !path.length) return "(unreachable)";
    return path.map(i => labels[i]).join(" → ");
  }

  function arraysEqual(a, b) {
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  }

  // Geometry helpers
  const NODE_R = 14;
  const TRIM_AT_NODE = NODE_R + 8;
  const LONG_NEAR_THRESHOLD = 20;
  const ADJ_VS_LONG_THRESHOLD = 18;
  const CROSS_OK_DEG = 25;

  function clamp(v, lo, hi) {
    return Math.max(lo, Math.min(hi, v));
  }

  function trimSegment(x1, y1, x2, y2, t1 = TRIM_AT_NODE, t2 = TRIM_AT_NODE) {
    const dx = x2 - x1, dy = y2 - y1;
    const L = Math.hypot(dx, dy) || 1;
    const ux = dx / L, uy = dy / L;
    const sX = x1 + ux * t1, sY = y1 + uy * t1, eX = x2 - ux * t2, eY = y2 - uy * t2;
    if ((t1 + t2) >= L - 1) {
      const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
      return { sx: mx, sy: my, ex: mx, ey: my };
    }
    return { sx: sX, sy: sY, ex: eX, ey: eY };
  }

  function segDist(x1, y1, x2, y2, x3, y3, x4, y4) {
    const EPS = 1e-9;
    let ux = x2 - x1, uy = y2 - y1, vx = x4 - x3, vy = y4 - y3, wx = x1 - x3, wy = y1 - y3;
    const a = ux * ux + uy * uy, b = ux * vx + uy * vy, c = vx * vx + vy * vy, d = ux * wx + uy * wy, e = vx * wx + vy * wy;
    const D = a * c - b * b;
    let sN, sD = D, tN, tD = D;
    if (D < EPS) {
      sN = 0; sD = 1; tN = e; tD = c;
    } else {
      sN = (b * e - c * d); tN = (a * e - b * d);
      if (sN < 0) { sN = 0; tN = e; tD = c; }
      else if (sN > sD) { sN = sD; tN = e + b; tD = c; }
    }
    if (tN < 0) {
      tN = 0;
      if (-d < 0) sN = 0;
      else if (-d > a) sN = sD;
      else { sN = -d; sD = a; }
    } else if (tN > tD) {
      tN = tD;
      if ((-d + b) < 0) sN = 0;
      else if ((-d + b) > a) sN = sD;
      else { sN = (-d + b); sD = a; }
    }
    const sc = (Math.abs(sN) < EPS ? 0 : sN / sD), tc = (Math.abs(tN) < EPS ? 0 : tN / tD);
    const dx = wx + (sc * ux) - (tc * vx), dy = wy + (sc * uy) - (tc * vy);
    return Math.hypot(dx, dy);
  }

  function orient(ax, ay, bx, by, cx, cy) {
    return (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
  }

  function onSeg(ax, ay, bx, by, cx, cy) {
    return Math.min(ax, bx) - 1e-9 <= cx && cx <= Math.max(ax, bx) + 1e-9 &&
      Math.min(ay, by) - 1e-9 <= cy && cy <= Math.max(ay, by) + 1e-9;
  }

  function segmentsIntersect(x1, y1, x2, y2, x3, y3, x4, y4) {
    const o1 = orient(x1, y1, x2, y2, x3, y3), o2 = orient(x1, y1, x2, y2, x4, y4);
    const o3 = orient(x3, y3, x4, y4, x1, y1), o4 = orient(x3, y3, x4, y4, x2, y2);
    if ((o1 > 0) !== (o2 > 0) && (o3 > 0) !== (o4 > 0)) return true;
    if (Math.abs(o1) < 1e-9 && onSeg(x1, y1, x2, y2, x3, y3)) return true;
    if (Math.abs(o2) < 1e-9 && onSeg(x1, y1, x2, y2, x4, y4)) return true;
    if (Math.abs(o3) < 1e-9 && onSeg(x3, y3, x4, y4, x1, y1)) return true;
    if (Math.abs(o4) < 1e-9 && onSeg(x3, y3, x4, y4, x2, y2)) return true;
    return false;
  }

  function segmentAngleDeg(x1, y1, x2, y2, x3, y3, x4, y4) {
    const ux = x2 - x1, uy = y2 - y1, vx = x4 - x3, vy = y4 - y3;
    const lu = Math.hypot(ux, uy), lv = Math.hypot(vx, vy);
    if (lu === 0 || lv === 0) return 0;
    let c = (ux * vx + uy * vy) / (lu * lv);
    c = clamp(c, -1, 1);
    return Math.acos(c) * 180 / Math.PI;
  }

  // Graph generation
  function generateOne(rng, N, E, middleLayers = 2) {
    N = Math.max(5, Math.min(50, N));
    middleLayers = Math.max(1, Math.min(6, middleLayers));
    const cols = middleLayers + 2;

    const counts = new Array(cols).fill(1);
    counts[0] = 1;
    counts[cols - 1] = 1;
    let remainN = N - counts.reduce((a, b) => a + b, 0);
    while (remainN > 0) {
      const c = randInt(rng, 1, cols - 2);
      counts[c]++;
      remainN--;
    }

    const nodes = [], labels = [], rankIdxs = [];
    for (let c = 0, i = 0; c < cols; c++) {
      rankIdxs[c] = [];
      for (let k = 0; k < counts[c]; k++, i++) {
        nodes.push({ id: i, rank: c, x: 0, y: 0 });
        if (c === cols - 1) {
          labels.push('X');
        } else {
          labels.push(labelForIndex(i));
        }
        rankIdxs[c].push(i);
      }
    }

    const W = 1200, H = 720, PAD = 70;
    const tierSpacing = (W - 2 * PAD) * 0.6;
    const startX = PAD + (W - 2 * PAD - tierSpacing) / 2;
    for (let c = 0; c < cols; c++) {
      const colX = startX + (c * tierSpacing) / (cols - 1);
      const m = rankIdxs[c].length;
      for (let j = 0; j < m; j++) {
        const nIdx = rankIdxs[c][j];
        const rowY = PAD + ((j + 1) * (H - 2 * PAD)) / (m + 1);
        const jitter = (m > 1) ? (rng() - 0.5) * 16 : 0;
        nodes[nIdx].x = colX;
        nodes[nIdx].y = rowY + jitter;
      }
    }

    const neighborCands = [], longCands = [];
    for (let c = 0; c < cols; c++) {
      if (c + 1 < cols) for (const u of rankIdxs[c]) for (const v of rankIdxs[c + 1]) neighborCands.push([u, v]);
      if (c + 2 < cols) for (const u of rankIdxs[c]) for (const v of rankIdxs[c + 2]) longCands.push([u, v]);
    }
    shuffle(rng, neighborCands);
    shuffle(rng, longCands);

    const edges = [], edgeSet = new Set(), longSegs = [];
    const outdeg = Array(N).fill(0), indeg = Array(N).fill(0);

    function addEdge(u, v, w, span) {
      const key = u + ">" + v;
      if (edgeSet.has(key)) return false;
      edges.push({ u, v, w, span });
      edgeSet.add(key);
      outdeg[u]++;
      indeg[v]++;
      if (span === 2) {
        const a = nodes[u], b = nodes[v];
        const t = trimSegment(a.x, a.y, b.x, b.y);
        longSegs.push({ sx: t.sx, sy: t.sy, ex: t.ex, ey: t.ey });
      }
      return true;
    }

    function longTooClose(u, v) {
      const a = nodes[u], b = nodes[v];
      const t = trimSegment(a.x, a.y, b.x, b.y);
      for (const e of edges) {
        const p = nodes[e.u], q = nodes[e.v];
        const s = trimSegment(p.x, p.y, q.x, q.y);
        if (segmentsIntersect(t.sx, t.sy, t.ex, t.ey, s.sx, s.sy, s.ex, s.ey)) {
          const ang = segmentAngleDeg(t.sx, t.sy, t.ex, t.ey, s.sx, s.sy, s.ex, s.ey);
          if (ang < CROSS_OK_DEG) return true;
        } else {
          const d = segDist(t.sx, t.sy, t.ex, t.ey, s.sx, s.sy, s.ex, s.ey);
          if (d < LONG_NEAR_THRESHOLD) return true;
        }
      }
      return false;
    }

    function adjTooCloseToLong(u, v) {
      if (longSegs.length === 0) return false;
      const a = nodes[u], b = nodes[v];
      const t = trimSegment(a.x, a.y, b.x, b.y);
      for (const s of longSegs) {
        if (segmentsIntersect(t.sx, t.sy, t.ex, t.ey, s.sx, s.sy, s.ex, s.ey)) {
          const ang = segmentAngleDeg(t.sx, t.sy, t.ex, t.ey, s.sx, s.sy, s.ex, s.ey);
          if (ang < CROSS_OK_DEG) return true;
        } else {
          const d = segDist(t.sx, t.sy, t.ex, t.ey, s.sx, s.sy, s.ex, s.ey);
          if (d < ADJ_VS_LONG_THRESHOLD) return true;
        }
      }
      return false;
    }

    const minNeighborRequired = Array.from({ length: cols - 1 }, (_, c) => Math.max(rankIdxs[c].length, rankIdxs[c + 1].length))
      .reduce((a, b) => a + b, 0);
    const baseTarget = Math.max(E, minNeighborRequired);
    const desiredLong = Math.min(longCands.length, Math.max(0, Math.round(baseTarget * 0.12)));
    const targetE = Math.min(neighborCands.length + longCands.length, baseTarget + desiredLong);

    for (let c = 0; c < cols - 1; c++) {
      const U = rankIdxs[c].slice();
      shuffle(makeRng('U' + c), U);
      const V = rankIdxs[c + 1].slice();
      shuffle(makeRng('V' + c), V);

      for (let j = 0; j < V.length; j++) {
        const u = U[j % U.length], v = V[j];
        addEdge(u, v, randInt(rng, 1, 15), 1);
      }
      const off = randInt(rng, 0, V.length - 1);
      for (let i = 0; i < U.length; i++) {
        const u = U[i];
        if (outdeg[u] === 0) {
          for (let t = 0; t < V.length; t++) {
            const v = V[(off + i + t) % V.length];
            if (addEdge(u, v, randInt(rng, 1, 15), 1)) break;
          }
        }
      }
    }

    let longAdded = 0;
    for (const [u, v] of longCands) {
      if (edges.length >= targetE) break;
      if (longAdded >= desiredLong) break;
      if (!longTooClose(u, v)) {
        if (addEdge(u, v, randInt(rng, 1, 15), 2)) longAdded++;
      }
    }

    for (const [u, v] of neighborCands) {
      if (edges.length >= targetE) break;
      if (adjTooCloseToLong(u, v)) continue;
      addEdge(u, v, randInt(rng, 1, 15), 1);
    }

    const adj = Array.from({ length: N }, () => []);
    for (let i = 0; i < edges.length; i++) {
      const e = edges[i];
      if (e.u >= N || e.v >= N || e.u < 0 || e.v < 0) continue;
      adj[e.u].push({ to: e.v, w: e.w, id: i });
    }

    return { N, nodes, labels, edges, adj, cols, rankIdxs, W, H, PAD };
  }

  function rebuildAdjacency(g) {
    const N = g.N;
    const edges = g.edges;
    const adj = Array.from({ length: N }, () => []);
    for (let i = 0; i < edges.length; i++) {
      const e = edges[i];
      if (e.u >= N || e.v >= N || e.u < 0 || e.v < 0) continue;
      adj[e.u].push({ to: e.v, w: e.w, id: i });
    }
    g.adj = adj;
  }

  // Shortest paths
  function reconstructPath(prev, t) {
    if (t == null) return [];
    const out = [];
    let cur = t, guard = 10000;
    const N = prev.length;
    if (t >= N || t < 0) return [];

    while (cur != null && guard-- > 0) {
      if (cur >= N || cur < 0) break;
      out.push(cur);
      if (prev[cur] == null) break;
      if (prev[cur] >= N || prev[cur] < 0) break;
      cur = prev[cur];
    }

    out.reverse();
    return out;
  }

  function dijkstraToTarget(g, s, target) {
    const N = g.N, INF = 1e15, dist = Array(N).fill(INF), prev = Array(N).fill(null);
    if (s >= N || target >= N) return { path: [], cost: Infinity, end: target, dist, prev };

    dist[s] = 0;
    const used = Array(N).fill(false);

    for (let it = 0; it < N; it++) {
      let u = -1, best = INF;
      for (let i = 0; i < N; i++) {
        if (!used[i] && dist[i] < best) {
          best = dist[i];
          u = i;
        }
      }
      if (u === -1) break;
      used[u] = true;

      if (!g.adj[u]) continue;
      for (const e of g.adj[u]) {
        const v = e.to, nd = dist[u] + e.w;
        if (v >= N || v < 0) continue;
        if (nd < dist[v]) {
          dist[v] = nd;
          prev[v] = u;
        }
      }
    }

    const path = (dist[target] < INF) ? reconstructPath(prev, target) : [];
    return { path, cost: (dist[target] < INF ? dist[target] : Infinity), end: target, dist, prev };
  }

  function greedyToTarget(g, s, target) {
    const N = g.N;
    if (s >= N || target >= N || s < 0 || target < 0) return { path: [], cost: Infinity, end: null };

    const path = [s];
    let cur = s, cost = 0, guard = 10000;
    while (cur !== target && guard-- > 0) {
      if (cur >= N || cur < 0) return { path: [], cost: Infinity, end: null };
      if (!g.adj[cur]) return { path: [], cost: Infinity, end: null };

      const outs = g.adj[cur].slice();
      if (outs.length === 0) return { path: [], cost: Infinity, end: null };

      for (const edge of outs) {
        if (edge.to >= N || edge.to < 0) continue;
      }

      outs.sort((a, b) => (a.w - b.w) || (g.labels[a.to] < g.labels[b.to] ? -1 : 1));
      const e = outs[0];

      if (e.to >= N || e.to < 0) return { path: [], cost: Infinity, end: null };

      cost += e.w;
      cur = e.to;
      path.push(cur);
      if (path.length > g.N + 5) break;
    }
    return cur === target ? { path, cost, end: cur } : { path: [], cost: Infinity, end: null };
  }

  function bellmanFordToTarget(g, s, target) {
    const N = g.N, INF = 1e15, dist = Array(N).fill(INF), prev = Array(N).fill(null), key = Array(N).fill(null);
    dist[s] = 0;
    key[s] = g.labels[s];
    for (let i = 0; i < N - 1; i++) {
      let any = false;
      for (const e of g.edges) {
        const u = e.u, v = e.v;
        if (dist[u] === INF) continue;
        const nd = dist[u] + e.w, nk = (key[u] ?? "") + g.labels[v];
        if (nd < dist[v]) {
          dist[v] = nd;
          prev[v] = u;
          key[v] = nk;
          any = true;
        } else if (nd === dist[v] && (nk ?? "") > (key[v] ?? "")) {
          prev[v] = u;
          key[v] = nk;
          any = true;
        }
      }
      if (!any) break;
    }
    const path = (dist[target] < INF) ? reconstructPath(prev, target) : [];
    return { path, cost: (dist[target] < INF ? dist[target] : Infinity), end: target, dist, prev };
  }

  function computeAll(g) {
    const A = 0, X = g.rankIdxs[g.cols - 1][0];
    if (A >= g.N || X >= g.N) return { dijkstra: { path: [], cost: Infinity }, greedy: { path: [], cost: Infinity }, bellman: { path: [], cost: Infinity }, target: X };

    const dijkstra = dijkstraToTarget(g, A, X);
    const greedy = greedyToTarget(g, A, X);
    const bellman = bellmanFordToTarget(g, A, X);

    return { dijkstra, greedy, bellman, target: X };
  }

  function countShortestPaths(g, dist, s) {
    const N = g.N, cnt = Array(N).fill(0);
    cnt[s] = 1;
    const order = [...Array(N).keys()].sort((a, b) => (dist[a] - dist[b]) || (a - b));
    for (const u of order) {
      if (!isFinite(dist[u]) || cnt[u] === 0) continue;
      for (const e of g.adj[u]) {
        const v = e.to;
        if (dist[u] + e.w === dist[v]) cnt[v] += cnt[u];
      }
    }
    return cnt;
  }

  function pathEdgesFromNodes(g, nodesPath) {
    const ed = [];
    if (!nodesPath || nodesPath.length < 2) return ed;
    for (let i = 0; i < nodesPath.length - 1; i++) {
      const u = nodesPath[i], v = nodesPath[i + 1];
      const e = g.edges.find(e => e.u === u && e.v === v);
      if (e) ed.push(e);
    }
    return ed;
  }

  function ensureUniqueAndGreedyWorse(g, rng) {
    const X = g.rankIdxs[g.cols - 1][0];
    const A = 0;
    let safety = 0;
    while (safety++ < 60) {
      let alg = computeAll(g);
      if (!isFinite(alg.dijkstra.cost)) return { ok: false };

      const d = alg.dijkstra;
      const chosenEnd = X;

      const cnt = countShortestPaths(g, d.dist, A);
      if (cnt[chosenEnd] > 1) {
        const dPathSet = new Set(pathEdgesFromNodes(g, d.path).map(e => e.u + ">" + e.v));
        let bumped = false;
        for (const e of g.edges) {
          if (d.dist[e.u] + e.w === d.dist[e.v]) {
            const key = e.u + ">" + e.v;
            if (!dPathSet.has(key) && e.w < 15) {
              e.w++;
              bumped = true;
              break;
            }
          }
        }
        if (bumped) {
          rebuildAdjacency(g);
          continue;
        } else return { ok: false };
      }

      if (!(isFinite(alg.greedy.cost) && alg.greedy.cost > d.cost)) {
        const gEdges = pathEdgesFromNodes(g, alg.greedy.path);
        const dSet = new Set(pathEdgesFromNodes(g, d.path).map(e => e.u + ">" + e.v));
        let changed = false;
        for (const e of gEdges) {
          const key = e.u + ">" + e.v;
          if (!dSet.has(key) && e.w < 15) {
            e.w += Math.max(1, Math.min(3, 15 - e.w));
            changed = true;
            break;
          }
        }
        if (changed) {
          rebuildAdjacency(g);
          continue;
        }
        return { ok: false };
      }

      balanceAlternativePaths(g, rng);
      alg = computeAll(g);

      if (!isFinite(alg.dijkstra.cost) || alg.dijkstra.path.length === 0) {
        continue;
      }

      return { ok: true };
    }
    return { ok: false };
  }

  function balanceAlternativePaths(g, rng) {
    const A = 0, X = g.rankIdxs[g.cols - 1][0];
    if (A >= g.N || X >= g.N) return;

    const alg = computeAll(g);
    const optimalCost = alg.dijkstra.cost;
    if (!isFinite(optimalCost)) return;

    const allPaths = [];

    function findAllPaths(current, target, path, cost, visited) {
      if (current >= g.N) return;
      if (current === target) {
        allPaths.push({ path: path.slice(), cost, edges: pathEdgesFromNodes(g, path) });
        return;
      }
      if (path.length > g.N || visited.has(current)) return;

      visited.add(current);
      for (const edge of g.adj[current]) {
        if (edge.to >= g.N) continue;
        path.push(edge.to);
        findAllPaths(edge.to, target, path, cost + edge.w, visited);
        path.pop();
      }
      visited.delete(current);
    }

    findAllPaths(A, X, [A], 0, new Set());
    allPaths.sort((a, b) => a.cost - b.cost);

    const optimalPath = allPaths[0];
    const optimalEdgeSet = new Set(optimalPath.edges.map(e => e.u + ">" + e.v));

    for (let i = 1; i < Math.min(allPaths.length, 5); i++) {
      const altPath = allPaths[i];
      const targetCost = optimalCost + randInt(rng, 1, 3);
      const currentCost = altPath.cost;

      if (currentCost > targetCost) {
        const reduction = currentCost - targetCost;
        const uniqueEdges = altPath.edges.filter(e => !optimalEdgeSet.has(e.u + ">" + e.v));

        if (uniqueEdges.length > 0) {
          const reductionPerEdge = Math.floor(reduction / uniqueEdges.length);
          let remainingReduction = reduction % uniqueEdges.length;

          for (const edge of uniqueEdges) {
            const maxReduction = Math.min(edge.w - 1, reductionPerEdge + (remainingReduction > 0 ? 1 : 0));
            edge.w -= maxReduction;
            if (remainingReduction > 0) remainingReduction--;
          }
        }
      }
    }

    rebuildAdjacency(g);
  }

  function generateGraphSatisfying(N, E, seedStr, middleLayers = 2, maxAttempts = 500) {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const rng = makeRng(seedStr, attempt);
      const g = generateOne(rng, N, E, middleLayers);
      const enforced = ensureUniqueAndGreedyWorse(g, rng);
      if (enforced.ok) {
        return { g, attempt };
      }
    }
    return null;
  }

  // Graphviz rendering
  function initGraphviz() {
    if (!window.d3 || !window.d3.select) {
      console.log('D3 not available');
      return false;
    }

    if (!graphvizRendererRef.current && graphvizContainerRef.current) {
      try {
        console.log('Initializing graphviz renderer...');
        const selection = window.d3.select(graphvizContainerRef.current);

        if (typeof selection.graphviz !== 'function') {
          console.log('Graphviz method not available on d3 selection');
          return false;
        }

        graphvizRendererRef.current = selection.graphviz();
        console.log('Graphviz renderer initialized successfully');
      } catch (error) {
        console.error('Error initializing graphviz:', error);
        return false;
      }
    }
    return !!graphvizRendererRef.current;
  }

  function generateDOT(g, solutionPathNodes = null) {
    const solSet = new Set();
    if (solutionPathNodes && solutionPathNodes.length > 1) {
      for (let i = 0; i < solutionPathNodes.length - 1; i++) {
        const u = solutionPathNodes[i], v = solutionPathNodes[i + 1];
        solSet.add(u + ">" + v);
      }
    }

    let dot = 'digraph G {\n';
    dot += '  rankdir=LR;\n';
    dot += '  ranksep=2.0;\n';
    dot += '  nodesep=1.0;\n';
    dot += '  node [shape=circle, style=filled, fillcolor=white, color=black, fontname="ui-monospace,monospace", fontsize=13];\n';
    dot += '  edge [color=black, fontname="ui-monospace,monospace", fontsize=12];\n';
    dot += '\n';

    for (let tier = 0; tier < g.cols; tier++) {
      const tierNodes = g.rankIdxs[tier];
      if (tierNodes.length > 0) {
        dot += `  { rank=same; `;
        for (const nodeId of tierNodes) {
          dot += `"${g.labels[nodeId]}" `;
        }
        dot += `}\n`;
      }
    }
    dot += '\n';

    for (const e of g.edges) {
      const fromLabel = g.labels[e.u];
      const toLabel = g.labels[e.v];
      const isSolution = solSet.has(e.u + ">" + e.v);

      if (isSolution) {
        dot += `  "${fromLabel}" -> "${toLabel}" [label="${e.w}", penwidth=3, color=black, labeldistance=2, labelangle=15];\n`;
      } else {
        dot += `  "${fromLabel}" -> "${toLabel}" [label="${e.w}", labeldistance=2, labelangle=15];\n`;
      }
    }

    dot += '}\n';
    return dot;
  }

  async function drawGraph(g, solutionPathNodes = null) {
    if (!initGraphviz()) {
      console.log('Graphviz not ready yet');
      return;
    }

    const dotString = generateDOT(g, solutionPathNodes);
    console.log('Rendering DOT string...');

    return new Promise((resolve) => {
      const timeoutId = setTimeout(() => {
        console.log('Graphviz render timeout');
        resolve();
      }, 3000);

      try {
        graphvizRendererRef.current
          .renderDot(dotString)
          .on("end", () => {
            console.log('Graphviz render completed');
            const svg = graphvizContainerRef.current?.querySelector('svg');
            if (svg) {
              svg.style.width = '100%';
              svg.style.height = '100%';
              svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
              console.log('SVG styled');
            }
            clearTimeout(timeoutId);
            resolve();
          });
      } catch (error) {
        console.error('Error rendering graphviz:', error);
        clearTimeout(timeoutId);
        resolve();
      }
    });
  }

  // Export functions
  function download(filename, dataUrlOrBlob) {
    const a = document.createElement('a');
    if (typeof dataUrlOrBlob === 'string') {
      a.href = dataUrlOrBlob;
    } else {
      a.href = URL.createObjectURL(dataUrlOrBlob);
    }
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      if (dataUrlOrBlob instanceof Blob) URL.revokeObjectURL(a.href);
    }, 0);
  }

  function exportTaskJSON(state) {
    const { g } = state;
    const payload = {
      task: "Min path sum from A to any rightmost node (directed left→right, positive weights).",
      N: g.N,
      E: g.edges.length,
      seed: state.seedStr,
      cols: g.cols,
      nodes: g.nodes.map(n => ({ id: n.id, label: g.labels[n.id], rank: n.rank, x: Math.round(n.x), y: Math.round(n.y) })),
      edges: g.edges.map(e => ({ from: g.labels[e.u], to: g.labels[e.v], weight: e.w, span: e.span })),
      rightmost: g.rankIdxs[g.cols - 1].map(i => g.labels[i]),
      guarantees: {
        connectivity: "Each non-leftmost node has ≥1 incoming from previous tier; each non-rightmost node has ≥1 outgoing to next tier.",
        uniqueShortestPath: true,
        greedyWorse: true
      }
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    download(`graph_task_${g.N}n_${g.edges.length}e.json`, blob);
  }

  async function exportGraphPNG(filename) {
    const svg = graphvizContainerRef.current?.querySelector('svg');
    if (!svg) return;

    const svgClone = svg.cloneNode(true);
    const viewBox = svgClone.viewBox.baseVal;
    let svgWidth, svgHeight;

    if (viewBox.width && viewBox.height) {
      svgWidth = viewBox.width;
      svgHeight = viewBox.height;
    } else {
      svgWidth = parseFloat(svgClone.getAttribute('width')) || 400;
      svgHeight = parseFloat(svgClone.getAttribute('height')) || 300;
    }

    const maxHeight = 1600;
    const scale = Math.min(maxHeight / svgHeight, 4);

    const padding = 20;
    const canvasWidth = Math.ceil(svgWidth * scale) + (padding * 2);
    const canvasHeight = Math.ceil(svgHeight * scale) + (padding * 2);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const svgData = new XMLSerializer().serializeToString(svgClone);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    return new Promise((resolve) => {
      img.onload = function() {
        const scaledWidth = svgWidth * scale;
        const scaledHeight = svgHeight * scale;
        ctx.drawImage(img, padding, padding, scaledWidth, scaledHeight);

        canvas.toBlob(function(blob) {
          if (blob) {
            download(filename, blob);
            URL.revokeObjectURL(url);
            resolve();
          }
        }, 'image/png');
      };

      img.onerror = function() {
        URL.revokeObjectURL(url);
        resolve();
      };

      img.src = url;
    });
  }

  // Main generation function
  async function generateAndRender() {
    const N = config.nodes;
    const E = config.edges;
    const middleLayers = config.layers;
    const seedStr = config.seed.toString();

    const res = generateGraphSatisfying(N, E, seedStr, middleLayers, 600);
    if (!res) {
      setOutput("Failed to generate a graph satisfying the constraints (unique optimal path and greedy worse). Try increasing N or E.");
      return;
    }

    const newState = {
      g: res.g,
      seedStr,
      showingSolution: false,
      alg: computeAll(res.g)
    };

    setAppState(newState);
    await drawGraph(newState.g, null);
    renderText(newState, false);
  }

  function renderText(state, showSolution) {
    const g = state.g, labels = g.labels;
    const alg = computeAll(g);

    const dPathStr = pathToLabelString(alg.dijkstra.path, labels), dCost = alg.dijkstra.cost;
    const grPathStr = pathToLabelString(alg.greedy.path, labels), grCost = alg.greedy.cost;
    const bfPathStr = pathToLabelString(alg.bellman.path, labels), bfCost = alg.bellman.cost;

    const unique = arraysEqual(alg.bellman.path, alg.dijkstra.path) && isFinite(dCost);

    const lines = [];
    lines.push(`Target node: ${labels[alg.target]}`);
    lines.push("");
    lines.push(`Dijkstra (ground truth, unique):`);
    lines.push(`  path: ${dPathStr}`);
    lines.push(`  cost: ${isFinite(dCost) ? dCost : "∞"}`);
    lines.push("");
    lines.push(`Greedy (locally smallest outgoing weight):`);
    lines.push(`  path: ${grPathStr}`);
    lines.push(`  cost: ${isFinite(grCost) ? grCost : "∞"}  ${isFinite(grCost) && isFinite(dCost) && grCost > dCost ? "✓ strictly worse" : "(!) should be worse"}`);
    lines.push("");
    lines.push(`Bellman—Ford (should match Dijkstra when unique):`);
    lines.push(`  path: ${bfPathStr}`);
    lines.push(`  cost: ${isFinite(bfCost) ? bfCost : "∞"}  ${arraysEqual(alg.bellman.path, alg.dijkstra.path) ? "✓ matches" : "(!) differs"}`);
    lines.push("");
    lines.push(`Unique optimum: ${unique ? "✓ enforced" : "(!) check"}`);
    lines.push(`Tip: "New seed & run" produces a fresh random instance; "Show solution" toggles the thick path highlight.`);
    setOutput(lines.join("\n"));
  }

  function randomSeed() {
    if (window.crypto && crypto.getRandomValues) {
      const a = new Uint32Array(2);
      crypto.getRandomValues(a);
      return a[0].toString(36) + '-' + a[1].toString(36);
    }
    return Math.random().toString(36).slice(2);
  }

  const handleGenerate = () => {
    generateAndRender();
  };

  const handleNewSeed = () => {
    const s = randomSeed();
    setConfig({ ...config, seed: s });
    setTimeout(generateAndRender, 50);
  };

  const handleToggleSolution = async () => {
    if (!appState.g) return;
    const newShowingSolution = !appState.showingSolution;
    setAppState({ ...appState, showingSolution: newShowingSolution });
    await drawGraph(appState.g, newShowingSolution ? appState.alg.dijkstra.path : null);
    renderText(appState, newShowingSolution);
  };

  const handleExportTask = () => {
    if (!appState.g) return;
    exportGraphPNG(`task_${appState.g.N}n_${appState.g.edges.length}e.png`);
  };

  const handleExportSolution = async () => {
    if (!appState.g) return;
    setExporting(true);

    try {
      if (!appState.showingSolution) {
        await handleToggleSolution();
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      await exportGraphPNG(`solution_${appState.g.N}n_${appState.g.edges.length}e.png`);
    } finally {
      setExporting(false);
    }
  };

  const handleExportJSON = () => {
    if (appState.g) exportTaskJSON(appState);
  };

  // Initialize graph when libraries are ready
  useEffect(() => {
    if (librariesReady && graphvizContainerRef.current) {
      console.log('Libraries ready, generating initial graph...');
      generateAndRender();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [librariesReady]);

  return (
    <div className="dijkstra-generator">
      <header>
        <h1>Left→Right Random Weighted Graph — Unique min path from A to X</h1>
        {loadError && (
          <div style={{
            padding: '10px',
            margin: '10px 0',
            background: '#fee',
            border: '1px solid #c00',
            borderRadius: '4px',
            color: '#c00'
          }}>
            {loadError}
          </div>
        )}
        <div className="toolbar">
          <div className="group">
            <label htmlFor="nodes">N nodes</label>
            <input
              id="nodes"
              type="number"
              min="5"
              max="50"
              value={config.nodes}
              onChange={(e) => setConfig({ ...config, nodes: parseInt(e.target.value) })}
              onKeyDown={(e) => e.key === 'Enter' && handleGenerate()}
            />
          </div>
          <div className="group">
            <label htmlFor="edges">E edges (min)</label>
            <input
              id="edges"
              type="number"
              min="4"
              max="200"
              value={config.edges}
              onChange={(e) => setConfig({ ...config, edges: parseInt(e.target.value) })}
              onKeyDown={(e) => e.key === 'Enter' && handleGenerate()}
            />
          </div>
          <div className="group">
            <label htmlFor="layers">Middle layers</label>
            <input
              id="layers"
              type="number"
              min="1"
              max="6"
              value={config.layers}
              onChange={(e) => setConfig({ ...config, layers: parseInt(e.target.value) })}
              onKeyDown={(e) => e.key === 'Enter' && handleGenerate()}
            />
          </div>
          <div className="group">
            <label htmlFor="seed">Seed</label>
            <input
              id="seed"
              type="text"
              value={config.seed}
              onChange={(e) => setConfig({ ...config, seed: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && handleGenerate()}
            />
          </div>
          <button onClick={handleGenerate} disabled={!librariesReady}>
            {librariesReady ? 'Generate task' : 'Loading...'}
          </button>
          <button onClick={handleNewSeed} disabled={!librariesReady}>New seed & run</button>
          <button onClick={handleToggleSolution} disabled={!librariesReady}>
            {appState.showingSolution ? 'Hide solution' : 'Show solution'}
          </button>
          <button onClick={handleExportTask} disabled={!librariesReady}>Export task</button>
          <button onClick={handleExportSolution} disabled={exporting || !librariesReady}>
            {exporting ? 'Exporting...' : 'Export solution image'}
          </button>
          <button onClick={handleExportJSON} disabled={!librariesReady}>Export JSON</button>
        </div>
        <div className="small">
          <strong>Guarantees:</strong> unique optimal route (no ties) and the greedy route is strictly worse. Most edges are between neighboring tiers; a few long edges (span two tiers) are spaced to avoid clutter. All nodes are reachable left→right to X.
        </div>
      </header>

      <main>
        <section className="stage">
          <div className="cy">
            <div ref={graphvizContainerRef} className="graphviz-container"></div>
          </div>
        </section>
        <aside>
          <div className="legend">
            <div>
              <span className="swatch"></span>
              <strong>Dijkstra path</strong> (thick black when shown)
            </div>
            <div className="small">
              Nodes are A, B, C, … X  Weights are 1—15. Arrows point left→right.
            </div>
          </div>
          <div className="result">{output}</div>
          <div className="note">
            <p>
              <strong>Task.</strong> Find the minimum path sum from node <strong>A</strong> to node <strong>X</strong> (rightmost node). The instance is generated so the optimal path is unique and beats a greedy choice.
            </p>
            <p>
              <strong>Algorithms.</strong> Dijkstra (ground truth), Greedy (pick smallest outgoing edge each step), and Bellman—Ford (should match Dijkstra on cost and path since the optimum is unique).
            </p>
          </div>
        </aside>
      </main>
    </div>
  );
};

export default DijkstraGenerator;
